/**
 * `playerView` — the redacted projection of a `GameState` for one player (SPEC.md §4.4).
 *
 * One source of truth: **`CardInstance.knownTo`**. A view carries a card's `defId` when the
 * viewer's bit is set in that instance's mask, and `null` otherwise. Nothing else in the
 * projection may depend on a hidden identity, which is what makes the view safe to hand to
 * the UI (ticket 06) and to the AI worker (ticket 21, SPEC.md §6.4: "the worker receives
 * only the AI's `playerView` — it cannot cheat by construction").
 *
 * ## What the view redacts
 *
 * 1. **Card identities** — `ViewInstance.defId` is `null` outside the viewer's `knownTo`.
 * 2. **Deck order** — knowing *what* a card is and knowing *where in a shuffled deck it is*
 *    are different facts. `knownTo` is the first; the engine has no representation for the
 *    second yet (wayfinder ticket 006 calls it "public-order markers for deck positions"),
 *    so a view never exposes a deck card's identity positionally: deck instance ids are
 *    listed in ascending order and every deck `defId` reads `null`. The identity knowledge
 *    itself is not lost — it surfaces as `PlayerZonesView.knownDeckDefIds`, the multiset of
 *    cards the viewer knows are *somewhere* in that deck. That is exactly what a player
 *    knows after a card is revealed and then shuffled away. The two cases position *is*
 *    known are `isBeingLookedAt` (a CR §7.9 look, transient) and `isRevealedDeckTop` (a
 *    face-up deck top under a live `reveal-deck-top` static, continuous).
 * 3. **The opponent's options** — an opponent's pending `ChoiceRequest` keys its hand
 *    options by `defId` (`play:d42`), so the option list is itself hidden information. A
 *    view carries the request's `kind` and `player` but only carries `options` when the
 *    viewer is the one being asked.
 * 4. **Events** — a `GameEvent` names `defId`s unconditionally (they are the engine's own
 *    view); `ViewEvent` nulls out every `defId` the viewer may not know, judged by what the
 *    viewer knows *now*, after the transition.
 * 5. **The RNG** — `GameState.rng` is absent from the view. Its counter would let a viewer
 *    predict future shuffles and draws.
 */
import { cloneIr } from './abilities/ir.ts';
import type { ChoiceRequest } from './choices.ts';
import { cloneAction } from './clone.ts';
import type { CostModifier } from './costs.ts';
import type { GameEvent } from './events.ts';
import type { InstanceId, PlayerIndex, Zone } from './ids.ts';
import { cloneModifier, deckTopIsRevealed } from './modifiers.ts';
import type {
  CardStack,
  Frame,
  GameResult,
  GameState,
  Modifier,
  PlayerState,
  RuleOptions,
  TurnPhase,
} from './state.ts';
import { instanceAt, playerAt, playerBit } from './state.ts';

/** One card as a viewer sees it: full public state, identity only where they know it. */
export interface ViewInstance {
  readonly id: InstanceId;
  readonly owner: PlayerIndex;
  readonly controller: PlayerIndex;
  readonly zone: Zone;
  readonly exerted: boolean;
  readonly damage: number;
  readonly drying: boolean;
  /**
   * The location this character is at, or `null` (CR §4.3.6). Public: both cards are in play
   * and everyone can see which characters stand at which location.
   */
  readonly location: InstanceId | null;
  /** The printed id, or `null` where this viewer does not know this card's identity. */
  readonly defId: number | null;
}

/** One player's zones as a viewer sees them. Every zone list is public structure. */
export interface PlayerZonesView {
  readonly index: PlayerIndex;
  /**
   * The instances in this deck, **ascending by id** — a deck's order is never in a view.
   */
  readonly deck: readonly InstanceId[];
  readonly hand: readonly InstanceId[];
  readonly play: readonly InstanceId[];
  readonly inkwell: readonly InstanceId[];
  readonly discard: readonly InstanceId[];
  /**
   * Cards under one of this player's cards in play — **not in play** (CR §8.10.4). Which
   * pile each belongs to is `PlayerView.stacks`; whether the viewer may know what one *is*
   * is the ordinary `defId` rule, so a facedown Boost card reads `null` for everybody.
   */
  readonly under: readonly InstanceId[];
  /**
   * Cards of this player's own deck they are currently looking at (CR §7.9). Public
   * structure — everyone can see that a player is looking at three cards — while *what* they
   * are follows the ordinary `defId` rule, so a private look names them only for the looker.
   */
  readonly looking: readonly InstanceId[];
  readonly lore: number;
  /** Cards inked this turn — public bookkeeping, projected as-is so a sampled state keeps it. */
  readonly inksThisTurn: number;
  /** Cards put into this inkwell this turn, however they got there — the same bookkeeping. */
  readonly cardsInkedThisTurn: number;
  /** Cards drawn this turn, however they got there — the same bookkeeping. */
  readonly cardsDrawnThisTurn: number;
  /** Cards put into this player's discard this turn, by any cause. */
  readonly cardsPutIntoDiscardThisTurn: number;
  /** Cards this player has discarded this turn (CR §7.3) — a hand→discard move only. */
  readonly cardsDiscardedThisTurn: number;
  readonly alteredHand: boolean;
  /**
   * Printed ids the viewer knows are *somewhere* in this deck, ascending (a multiset: a
   * card known twice appears twice). This is where revealed-then-shuffled knowledge lives.
   */
  readonly knownDeckDefIds: readonly number[];
}

/**
 * One triggered ability waiting in the bag, as a viewer sees it. The bag is a **public zone**
 * (CR §7.7.1): everyone can see which triggers are waiting, whose they are and which card
 * raised them, so the projection is structural — item id, controller, source — plus the
 * ordinary `defId` rule for naming the source. `item` is what a `bag-order:b<id>` option key
 * refers to, which is how the UI labels an ordering button with the trigger it resolves.
 */
export interface ViewBagItem {
  /** The bag item's id — the thing a `bag-order:b<id>` option key names. */
  readonly item: number;
  /** Who resolves it, and orders it against their others (CR §7.7.4). */
  readonly controller: PlayerIndex;
  /** The card whose ability triggered. */
  readonly source: InstanceId;
  /** The source's printed id, `null` where this viewer does not know it — the ordinary rule. */
  readonly defId: number | null;
  /** The printed ability name, where the card has one (`TriggerSource.label`). */
  readonly label: string;
}

/** A `GameEvent` with every `defId` made nullable — `null` means "redacted for you". */
export type ViewEvent = RedactedEvent<GameEvent>;

type RedactedEvent<E> = E extends { readonly defId: number }
  ? Omit<E, 'defId'> & { readonly defId: number | null }
  : E;

/**
 * Everything one player may know about a game in progress. Plain data, independent of the
 * state it came from, and safe to `postMessage` to a worker.
 */
export interface PlayerView {
  /** Whose view this is. */
  readonly viewer: PlayerIndex;
  readonly rules: RuleOptions;
  /** Dense and aligned with `GameState.instances`: `instances[id].id === id`. */
  readonly instances: readonly ViewInstance[];
  readonly players: readonly PlayerZonesView[];
  readonly startingPlayer: PlayerIndex;
  readonly activePlayer: PlayerIndex;
  readonly turn: number;
  readonly phase: TurnPhase;
  /**
   * The resolution stack. Frames are structural (instance ids, steps, counts) and name no
   * identities — a new `Frame` kind carrying a `defId` would have to be redacted here.
   */
  readonly stack: readonly Frame[];
  /**
   * The triggered abilities waiting in the bag, oldest first — the bag is a public zone
   * (CR §7.7.1). This is what lets the UI name a pending `bag-order` option: each option's
   * `b<id>` is a `ViewBagItem.item`. What a viewer may know about a source's *identity* is
   * the ordinary `defId` rule.
   */
  readonly bag: readonly ViewBagItem[];
  /**
   * Live continuous-effect records (SPEC.md §4.5). Public by construction — a modifier names
   * instance ids, a scope, a delta and (as IR) its duration and guard, all of which the
   * effect that created it announced — and the viewer needs them: a `ViewInstance` carries no
   * characteristics, so the UI and the AI recompute {S}/{W}/lore from the printed card plus
   * these and the statics of the cards in play.
   */
  readonly modifiers: readonly Modifier[];
  /**
   * The in-play card stacks (SPEC.md §4.1). Public structure by construction — a pile names
   * only instance ids, and which cards are stacked is something everyone can see on the
   * table — so a stack rides into every seat's view unredacted. What a viewer may know about
   * the *identity* of an under-card is the ordinary `ViewInstance.defId` rule: a Shift base
   * is face up and public, a Boost card is face down and reads `null` for everyone.
   */
  readonly stacks: readonly CardStack[];
  /**
   * Live payment modifiers (SPEC.md §4.1's cost system). Public for the same reason as
   * `modifiers` — a cost reduction names only a printed-card filter and a delta, and the
   * effect that created it was announced — and the UI needs them to show what a card in hand
   * will actually cost, which a `ViewInstance` cannot carry.
   */
  readonly costModifiers: readonly CostModifier[];
  /** Once-per-turn ability uses, as `i<instance>:a<index>` / `t<trigger>` keys. */
  readonly abilityUses: readonly string[];
  /** Characters that have quested this turn — public, and read by "has quested" abilities. */
  readonly questedThisTurn: readonly InstanceId[];
  /** The challenger of each challenge declared this turn — public, and counted by "the second challenge". */
  readonly challengedThisTurn: readonly InstanceId[];
  /** The defender of each challenge declared this turn — public, read by "was challenged this turn". */
  readonly wasChallengedThisTurn: readonly InstanceId[];
  readonly playedThisTurn: readonly InstanceId[];
  /** Cards banished in a challenge this turn — public, read by "was banished in a challenge". */
  readonly banishedInChallengeThisTurn: readonly InstanceId[];
  /** Characters/locations that had a card put under them this turn — public, read by "if you've put a card under". */
  readonly cardsPutUnderThisTurn: readonly InstanceId[];
  /** Cards that left any discard this turn — public, read by "left a player's discard this turn". */
  readonly leftDiscardThisTurn: readonly InstanceId[];
  /** Cards banished this turn by any cause — public, read by "if a character was banished this turn". */
  readonly banishedThisTurn: readonly InstanceId[];
  /** Characters/locations that took damage this turn — public, read by "if a character took damage this turn". */
  readonly damagedThisTurn: readonly InstanceId[];
  /** Damage removed from each player's characters this turn, indexed by player. */
  readonly damageRemovedThisTurn: readonly number[];
  /**
   * The pending decision. `options` is empty unless `pending.player === viewer`: an
   * opponent's option list is keyed by the cards in their hand.
   */
  readonly pending: ChoiceRequest;
  /** What the last `apply` emitted, redacted. */
  readonly events: readonly ViewEvent[];
  readonly result: GameResult | null;
}

/**
 * Whether `player` knows this instance's identity — the raw `knownTo` bit. Knowledge is
 * monotone: it is gained by seeing a card and never lost, not even by shuffling it away
 * (the shuffle costs you the card's *position*, not its identity).
 */
export function knowsCard(state: GameState, player: PlayerIndex, id: InstanceId): boolean {
  return (instanceAt(state, id).knownTo & playerBit(player)) !== 0;
}

/**
 * Whether a view for `player` may name this card. Identity knowledge *plus* knowing where
 * the card is: a card in a deck is never named, because its position is unknown even when
 * its identity is not (see the module docs).
 */
export function isVisibleTo(state: GameState, player: PlayerIndex, id: InstanceId): boolean {
  const instance = instanceAt(state, id);
  if (instance.zone === 'deck' && !isBeingLookedAt(state, id) && !isRevealedDeckTop(state, id)) {
    return false;
  }
  return knowsCard(state, player, id);
}

/**
 * Whether a card in a deck is one its owner is currently **looking at** (CR §7.9).
 *
 * This is the one exception to "a view never names a deck card", and it is exactly the
 * knowledge the act creates: looking at the top three cards of your deck tells you what they
 * are *and where they are*, which is why the position rule that hides every other deck card
 * does not apply to these. Who may name them is still `knownTo` — a private look sets only the
 * looker's bit, a public reveal sets everyone's.
 */
export function isBeingLookedAt(state: GameState, id: InstanceId): boolean {
  return state.players.some((player) => player.looking.includes(id));
}

/**
 * Whether a card in a deck is a **face-up top card** — the *continuous* exception to "a view
 * never names a deck card", beside `isBeingLookedAt`'s transient one. While a `reveal-deck-top`
 * static is live ("Each player plays with the top card of their deck face up"), the top card
 * of a covered deck is public knowledge, position and all; which card that is follows the deck
 * on every read, so a draw or a shuffle turns the next card face up by itself. Who may *name*
 * it is still `knownTo` — the engine's step loop reveals each new top card to everyone while
 * the static is live, and that knowledge outlives the static (`knownDeckDefIds`) exactly as a
 * one-shot reveal's does.
 */
export function isRevealedDeckTop(state: GameState, id: InstanceId): boolean {
  const instance = instanceAt(state, id);
  if (instance.zone !== 'deck') return false;
  if (playerAt(state, instance.controller).deck[0] !== id) return false;
  return deckTopIsRevealed(state, instance.controller);
}

/** Project a state through one player's knowledge. Never mutates, never aliases. */
export function playerView(state: GameState, viewer: PlayerIndex): PlayerView {
  return {
    viewer,
    rules: { ...state.rules },
    instances: state.instances.map((instance) => ({
      id: instance.id,
      owner: instance.owner,
      controller: instance.controller,
      zone: instance.zone,
      exerted: instance.exerted,
      damage: instance.damage,
      drying: instance.drying,
      location: instance.location,
      defId: isVisibleTo(state, viewer, instance.id) ? instance.defId : null,
    })),
    players: state.players.map((player) => viewPlayer(state, viewer, player)),
    startingPlayer: state.startingPlayer,
    activePlayer: state.activePlayer,
    turn: state.turn,
    phase: state.phase,
    stack: state.stack.map(copyFrame),
    bag: state.bag.items.map((item) => ({
      item: item.id,
      controller: item.controller,
      source: item.source,
      defId: isVisibleTo(state, viewer, item.source) ? instanceAt(state, item.source).defId : null,
      label: item.label,
    })),
    // Structural, not shallow: a record's scope, guard and dynamic amount are nested data.
    modifiers: state.modifiers.map(cloneModifier),
    stacks: state.stacks.map((stack) => ({
      top: stack.top,
      under: [...stack.under],
      shifted: [...stack.shifted],
    })),
    costModifiers: state.costModifiers.map((modifier) => cloneIr(modifier)),
    abilityUses: [...state.abilityUses],
    questedThisTurn: [...state.questedThisTurn],
    challengedThisTurn: [...state.challengedThisTurn],
    wasChallengedThisTurn: [...state.wasChallengedThisTurn],
    playedThisTurn: [...state.playedThisTurn],
    banishedInChallengeThisTurn: [...state.banishedInChallengeThisTurn],
    cardsPutUnderThisTurn: [...state.cardsPutUnderThisTurn],
    leftDiscardThisTurn: [...state.leftDiscardThisTurn],
    banishedThisTurn: [...state.banishedThisTurn],
    damagedThisTurn: [...state.damagedThisTurn],
    damageRemovedThisTurn: [...state.damageRemovedThisTurn],
    pending: {
      kind: state.pending.kind,
      player: state.pending.player,
      options:
        state.pending.player === viewer
          ? state.pending.options.map((each) => ({ key: each.key, action: cloneAction(each.action) }))
          : [],
    },
    events: state.events.map((event) => redactEvent(state, viewer, event)),
    result: state.result === null ? null : { ...state.result },
  };
}

function viewPlayer(state: GameState, viewer: PlayerIndex, player: PlayerState): PlayerZonesView {
  const knownDeckDefIds: number[] = [];
  for (const id of player.deck) {
    if (knowsCard(state, viewer, id)) knownDeckDefIds.push(instanceAt(state, id).defId);
  }
  knownDeckDefIds.sort((a, b) => a - b);
  return {
    index: player.index,
    // Ascending, not in deck order — the order of a deck is not the viewer's to know.
    deck: [...player.deck].sort((a, b) => a - b),
    hand: [...player.hand],
    play: [...player.play],
    inkwell: [...player.inkwell],
    discard: [...player.discard],
    under: [...player.under],
    looking: [...player.looking],
    lore: player.lore,
    inksThisTurn: player.inksThisTurn,
    cardsInkedThisTurn: player.cardsInkedThisTurn,
    cardsDrawnThisTurn: player.cardsDrawnThisTurn,
    cardsPutIntoDiscardThisTurn: player.cardsPutIntoDiscardThisTurn,
    cardsDiscardedThisTurn: player.cardsDiscardedThisTurn,
    alteredHand: player.alteredHand,
    knownDeckDefIds,
  };
}

/**
 * Redact one event. Events that name a card carry the instance too, so the same `knownTo`
 * test that redacts the board redacts the event stream — one source of truth.
 */
function redactEvent(state: GameState, viewer: PlayerIndex, event: GameEvent): ViewEvent {
  if (!('defId' in event)) return event;
  const named = 'instance' in event ? event.instance : event.source;
  const defId = isVisibleTo(state, viewer, named) ? event.defId : null;
  return { ...event, defId };
}

/**
 * Copy a resolution-stack frame without an exhaustive switch, so a new `Frame` kind lands
 * in a view unchanged rather than silently dropped.
 *
 * Frames are plain data all the way down — instance ids, step names and ability IR — so one
 * structural deep copy is both correct and future-proof. **A frame that carried a `defId`
 * would have to be redacted here**; today none does, which is why a new frame kind is safe by
 * construction (ticket 05's note to ticket 07, still true). The `custom()` transcript an
 * `effect` frame carries is held to the same rule at its own end: a recorded fact is a number,
 * a boolean or a list of instance ids, and a choice token names an instance, never a `defId`
 * (ticket 13; `abilities/custom.ts`).
 */
function copyFrame(frame: Frame): Frame {
  return cloneIr(frame);
}
